import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { app } from './firebase';

const db = getFirestore(app);

const SchedulerView = ({ user, employees, consultantDb, logAction, addToast }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '09:00',
    endTime: '10:00',
    attendees: [], // List of employee IDs
    consultantId: '',
    type: 'Single', // 'Single', 'Recurring'
    status: 'Scheduled', // 'Scheduled', 'Done', 'Cancelled', 'Delayed'
    notes: '',
    cancelReason: ''
  });

  // Pending items (Consultants needing check-ins)
  const [pendingCheckIns, setPendingCheckIns] = useState([]);
  
  // Filters
  const [filterConsultantId, setFilterConsultantId] = useState('');
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [showOnlyMySpocs, setShowOnlyMySpocs] = useState(true); // Default to true for SPOC focus

  // --- 1. DATA FETCHING ---
  useEffect(() => {
    if (!user) return;
    setLoading(true);

    // Fetch Meetings (All for now, can optimize by date range later)
    const q = query(collection(db, "meetings"));
    const unsub = onSnapshot(q, (snapshot) => {
      const ms = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setMeetings(ms);
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  // Derive Pending Check-ins from Consultant DB
  useEffect(() => {
    if (!consultantDb) return;
    
    // Find consultants with a nextCheckInDate that DON'T have a meeting scheduled on that date (simplification)
    // In a real app, we'd query to verify if a meeting exists for that specific check-in.
    // For now, we'll list them if the date is set.
    
    // Identify current employee ID for SPOC check
    const currentEmployee = employees.find(e => e.email === user.email);
    const currentEmpId = currentEmployee ? currentEmployee.id : null;

    let pending = consultantDb.filter(c => {
        if (!c.nextCheckInDate) return false;
        
        // SPOC Filter
        if (showOnlyMySpocs && currentEmpId) {
             // Assuming 'assigneeId' or similar tracks the SPOC/Owner in consultantDb. 
             // Checking KanbanBoardView, it uses 'assigneeId' on items. 
             // Consultant DB might not have 'assigneeId' directly if it's just the DB.
             // Usually, the active contract or the recruitment card has the assignee.
             // However, let's check if 'ownerId' or 'reponsibleId' exists, or fallback to showing all if specific field missing.
             // For now, let's assume 'recruiterId' or we rely on the Kanban Items linkage. 
             // If strict SPOC data isn't in consultant_db, we might skip this or check if name matches.
             
             // Simplification: Check if the consultant has a field 'assigneeId' (common pattern) 
             // OR if we can match against an employee list.
             // Let's rely on the user to toggle off if data isn't perfect.
             if (c.assigneeId && c.assigneeId !== currentEmpId) return false;
        }

        // Check if there's already a meeting for this consultant on this date
        const hasMeeting = meetings.some(m => 
            m.consultantId === c.id && 
            m.date === c.nextCheckInDate && 
            m.status !== 'Cancelled'
        );
        return !hasMeeting;
    }).map(c => ({
        id: `pending_${c.id}`,
        isPending: true,
        consultantId: c.id,
        consultantName: `${c.firstName} ${c.lastName}`,
        date: c.nextCheckInDate,
        title: `Check-in: ${c.firstName}`,
        status: 'Pending Time'
    }));

    setPendingCheckIns(pending);
  }, [consultantDb, meetings, showOnlyMySpocs, user, employees]);


  // --- 2. CALENDAR LOGIC ---
  const getWeekDays = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    start.setDate(diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d);
    }
    return days;
  };

  const weekDays = getWeekDays(currentDate);

  const prevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // --- 3. EVENT HANDLERS ---
  
  const handleSlotClick = (date, hour) => {
      const dateStr = date.toISOString().split('T')[0];
      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
      const endTimeStr = `${(hour + 1).toString().padStart(2, '0')}:00`;
      
      setFormData({
          title: '',
          date: dateStr,
          time: timeStr,
          endTime: endTimeStr,
          attendees: [user.uid], 
          consultantId: filterConsultantId || '', // Pre-fill if filtered
          type: 'Single',
          status: 'Scheduled',
          notes: '',
          cancelReason: ''
      });
      setSelectedMeeting(null);
      setShowModal(true);
  };

  const handleEventClick = (meeting) => {
      if (meeting.isPending) {
          // It's a pending check-in from the sidebar or top
          setFormData({
              title: meeting.title,
              date: meeting.date,
              time: '09:00',
              endTime: '10:00',
              attendees: [],
              consultantId: meeting.consultantId,
              type: 'Recurring',
              status: 'Scheduled',
              notes: '',
              cancelReason: ''
          });
      } else {
          // Existing meeting
          setFormData({
              ...meeting
          });
      }
      setSelectedMeeting(meeting);
      setShowModal(true);
  };

  const handleSave = async () => {
      try {
          // Basic Validation
          if (!formData.title || !formData.date || !formData.time) {
              alert("Please fill in required fields.");
              return;
          }

          const meetingData = {
              ...formData,
              updatedAt: new Date().toISOString(),
              updatedBy: user.email
          };

          if (selectedMeeting && !selectedMeeting.isPending) {
              // Update
              await updateDoc(doc(db, "meetings", selectedMeeting.id), meetingData);
              if (addToast) addToast("Meeting updated", "success");
          } else {
              // Create
              await addDoc(collection(db, "meetings"), meetingData);
              if (addToast) addToast("Meeting scheduled", "success");
              
              // If it was a pending check-in, we don't delete the consultant record, 
              // but the 'useEffect' will filter it out once a meeting exists on that date.
          }
          setShowModal(false);
      } catch (e) {
          console.error(e);
          alert("Error saving meeting");
      }
  };

  const handleStatusChange = async (newStatus, reason = null) => {
      if (!selectedMeeting || selectedMeeting.isPending) return;
      
      try {
          const updates = { status: newStatus };
          if (reason) updates.cancelReason = reason;

          // If 'Done' (Completed), we might want to trigger the logic to reset recurrence
          if (newStatus === 'Done') {
              // 1. Log Contact
              if (logAction) logAction("Meeting Completed", `Completed meeting: ${selectedMeeting.title}`);
              
              // 2. Reset Recurrence (Update Consultant nextCheckInDate)
              if (selectedMeeting.consultantId) {
                  const consultant = consultantDb.find(c => c.id === selectedMeeting.consultantId);
                  if (consultant) {
                      const nextDate = calculateNextDate(new Date(), consultant.checkInFrequency); // Implement this helper
                      await updateDoc(doc(db, "consultant_db", consultant.id), {
                          lastContactDate: new Date().toISOString(),
                          nextCheckInDate: nextDate
                      });
                      if (addToast) addToast("Consultant recurrence updated", "info");
                  }
              }
          }

          if (newStatus === 'Delayed') {
              // Clear date so it falls back to 'Pending' list (logic in useEffect will see no meeting on checkInDate)
              updates.date = ''; 
              updates.time = '';
              if (addToast) addToast("Meeting delayed. Moved back to pending queue.", "warning");
          }

          await updateDoc(doc(db, "meetings", selectedMeeting.id), updates);
          setShowModal(false);
      } catch (e) {
          console.error(e);
      }
  };

  const calculateNextDate = (fromDate, frequency) => {
      const d = new Date(fromDate);
      if (!frequency) return d.toISOString().split('T')[0]; // Default?
      
      const val = parseInt(frequency.value) || 1;
      const unit = frequency.unit || 'months';
      
      if (unit === 'days') d.setDate(d.getDate() + val);
      if (unit === 'weeks') d.setDate(d.getDate() + val * 7);
      if (unit === 'months') d.setMonth(d.getMonth() + val);
      
      return d.toISOString().split('T')[0];
  };

  // --- 4. RENDER HELPERS ---
  const getEventsForDay = (date) => {
      const dateStr = date.toISOString().split('T')[0];
      
      // 1. Real Meetings
      let real = meetings.filter(m => m.date === dateStr);
      
      // Filter Real Meetings
      if (filterConsultantId) {
          real = real.filter(m => m.consultantId === filterConsultantId);
      }
      if (filterEmployeeId) {
          real = real.filter(m => m.attendees && m.attendees.includes(filterEmployeeId));
      }

      // 2. Pending Check-ins (Only show if date matches)
      let pending = pendingCheckIns.filter(p => p.date === dateStr);
      
      // Filter Pending Items
      if (filterConsultantId) {
          pending = pending.filter(p => p.consultantId === filterConsultantId);
      }
      // Pending check-ins usually don't have attendees yet, so if filtering by employee, we might hide them 
      // OR we check if the consultant is assigned to that employee (if we had that data). 
      // For now, if employee filter is on, we hide pending unless we can link them.
      // To be safe/simple: Show pending only if NO employee filter OR filter matches the "assignee" (which we don't strictly have here).
      // Let's hide pending if employee filter is active, assuming users will clear filter to see general to-dos.
      if (filterEmployeeId) {
          pending = []; 
      }

      return [...real, ...pending];
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Done': return 'bg-success text-white';
          case 'Cancelled': return 'bg-danger text-white text-decoration-line-through';
          case 'Delayed': return 'bg-warning text-dark';
          case 'Pending Time': return 'bg-info text-dark border-dashed';
          default: return 'bg-primary text-white';
      }
  };

  return (
    <div className="h-100 d-flex flex-column">
      {loading && <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center" style={{zIndex: 2000}}>
        <div className="spinner-border text-primary" role="status"></div>
      </div>}
      {/* TOOLBAR */}
      <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-card border-theme rounded shadow-sm">
          <div className="d-flex align-items-center gap-3">
              <h4 className="mb-0 fw-bold text-main">Scheduler</h4>
              <div className="btn-group">
                  <button className="btn btn-outline-secondary btn-sm" onClick={prevWeek}><i className="bi bi-chevron-left"></i></button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={goToToday}>Today</button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={nextWeek}><i className="bi bi-chevron-right"></i></button>
              </div>
              <h5 className="mb-0 text-muted-theme ms-2">
                  {weekDays[0].toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
              </h5>
          </div>
          
          <div className="d-flex gap-2 align-items-center">
             
             {/* FILTERS */}
             <select className="form-select form-select-sm bg-input text-main border-theme w-auto" value={filterConsultantId} onChange={e => setFilterConsultantId(e.target.value)}>
                 <option value="">All Consultants</option>
                 {consultantDb.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
             </select>

             <select className="form-select form-select-sm bg-input text-main border-theme w-auto" value={filterEmployeeId} onChange={e => setFilterEmployeeId(e.target.value)}>
                 <option value="">All Employees</option>
                 {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
             </select>

             <span className="badge bg-warning text-dark d-flex align-items-center">
                 {pendingCheckIns.filter(p => new Date(p.date) < new Date()).length} Overdue
             </span>
          </div>
      </div>

      <div className="flex-grow-1 row g-0 overflow-hidden">
          {/* LEFT: PENDING / OVERDUE LIST */}
          <div className="col-md-2 bg-input border-end border-theme p-3 overflow-auto d-none d-md-block">
              <h6 className="text-uppercase text-muted-theme small fw-bold mb-3">To Schedule / Overdue</h6>
              
              <div className="form-check mb-3">
                  <input className="form-check-input" type="checkbox" checked={showOnlyMySpocs} onChange={e => setShowOnlyMySpocs(e.target.checked)} id="spocCheck" />
                  <label className="form-check-label small text-main" htmlFor="spocCheck">My Consultants Only</label>
              </div>

              {pendingCheckIns.filter(p => !p.isScheduled).length === 0 && <p className="text-muted-theme small">No pending check-ins.</p>}
              
              {pendingCheckIns.map(item => {
                  const isOverdue = new Date(item.date) < new Date().setHours(0,0,0,0);
                  const daysUntil = Math.ceil((new Date(item.date) - new Date().setHours(0,0,0,0)) / (1000 * 60 * 60 * 24));
                  const timeText = daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : (daysUntil === 0 ? 'Today' : `${daysUntil} days left`);

                  return (
                      <div 
                        key={item.id} 
                        className={`card mb-2 shadow-sm border-theme ${isOverdue ? 'border-danger' : ''}`}
                        onClick={() => handleEventClick(item)}
                        style={{cursor: 'pointer'}}
                      >
                          <div className="card-body p-2">
                              <div className="fw-bold text-main small">{item.consultantName}</div>
                              <div className={`small ${isOverdue ? 'text-danger fw-bold' : 'text-muted-theme'}`}>
                                  {new Date(item.date).toLocaleDateString()}
                              </div>
                              <div className="d-flex justify-content-between align-items-center mt-1">
                                  <span className="badge bg-info text-dark" style={{fontSize: '0.65rem'}}>Pending Time</span>
                                  <span style={{fontSize: '0.65rem'}} className={isOverdue ? 'text-danger' : 'text-muted-theme'}>{timeText}</span>
                              </div>
                          </div>
                      </div>
                  );
              })}
          </div>

          {/* MAIN CALENDAR GRID */}
          <div className="col-md-10 d-flex flex-column h-100 overflow-auto">
              
              {/* HEADER ROW */}
              <div className="d-flex border-bottom border-theme">
                  <div className="p-2 border-end border-theme bg-card" style={{width: '60px'}}></div> {/* Time Col */}
                  {weekDays.map((day, i) => (
                      <div key={i} className={`flex-grow-1 p-2 text-center border-end border-theme bg-card ${day.toDateString() === new Date().toDateString() ? 'bg-primary bg-opacity-10' : ''}`}>
                          <div className="fw-bold text-main">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                          <div className="small text-muted-theme">{day.getDate()}</div>
                      </div>
                  ))}
              </div>

              {/* TIME GRID */}
              <div className="flex-grow-1 overflow-auto position-relative" style={{height: '0px'}}> {/* height 0 to allow flex grow scroll */}
                  {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(hour => (
                      <div key={hour} className="d-flex border-bottom border-theme" style={{minHeight: '80px'}}>
                          <div className="p-2 border-end border-theme text-muted-theme small text-end bg-card" style={{width: '60px'}}>
                              {hour}:00
                          </div>
                          {weekDays.map((day, i) => {
                              const events = getEventsForDay(day).filter(e => {
                                  // Simple Time Filter: Check if event starts in this hour
                                  if (e.isPending && !e.time) return hour === 9; // Default pending to 9am slot for visual
                                  if (!e.time) return false;
                                  const h = parseInt(e.time.split(':')[0]);
                                  return h === hour;
                              });

                              return (
                                  <div 
                                    key={i} 
                                    className="flex-grow-1 border-end border-theme position-relative p-1 slot-hover"
                                    onClick={() => handleSlotClick(day, hour)}
                                  >
                                      {events.map((ev, idx) => (
                                          <div 
                                            key={idx}
                                            onClick={(e) => { e.stopPropagation(); handleEventClick(ev); }}
                                            className={`rounded p-1 small mb-1 shadow-sm text-truncate cursor-pointer ${getStatusColor(ev.status)}`}
                                            title={`${ev.title} (${ev.status})`}
                                          >
                                              <div className="fw-bold">{ev.time || '??:??'} {ev.title}</div>
                                              {ev.consultantName && <div style={{fontSize: '0.7rem'}}>{ev.consultantName}</div>}
                                              {ev.isPending && <i className="bi bi-exclamation-circle-fill text-warning ms-1"></i>}
                                          </div>
                                      ))}
                                  </div>
                              );
                          })}
                      </div>
                  ))}
              </div>
          </div>
      </div>

      {/* MEETING MODAL */}
      {showModal && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070}}>
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content bg-card border-theme shadow-lg">
                      <div className="modal-header border-theme">
                          <h5 className="modal-title text-main">{selectedMeeting && !selectedMeeting.isPending ? 'Edit Meeting' : 'Schedule Meeting'}</h5>
                          <button className="btn-close" onClick={() => setShowModal(false)}></button>
                      </div>
                      <div className="modal-body">
                          <div className="mb-3">
                              <label className="small text-muted-theme">Title</label>
                              <input className="form-control bg-input text-main border-theme" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                          </div>
                          <div className="row g-2 mb-3">
                              <div className="col-6">
                                  <label className="small text-muted-theme">Date</label>
                                  <input type="date" className="form-control bg-input text-main border-theme" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                              </div>
                              <div className="col-3">
                                  <label className="small text-muted-theme">Start</label>
                                  <input type="time" className="form-control bg-input text-main border-theme" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} />
                              </div>
                              <div className="col-3">
                                  <label className="small text-muted-theme">End</label>
                                  <input type="time" className="form-control bg-input text-main border-theme" value={formData.endTime} onChange={e => setFormData({...formData, endTime: e.target.value})} />
                              </div>
                          </div>
                          
                          <div className="mb-3">
                              <label className="small text-muted-theme">Consultant (Optional)</label>
                              <select className="form-select bg-input text-main border-theme" value={formData.consultantId} onChange={e => setFormData({...formData, consultantId: e.target.value})}>
                                  <option value="">None</option>
                                  {consultantDb.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                              </select>
                          </div>

                          <div className="mb-3">
                              <label className="small text-muted-theme">Attendees (Employees)</label>
                              <select multiple className="form-select bg-input text-main border-theme" value={formData.attendees} onChange={e => setFormData({...formData, attendees: Array.from(e.target.selectedOptions, option => option.value)})}>
                                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                              </select>
                              <small className="text-muted-theme">Hold Ctrl/Cmd to select multiple</small>
                          </div>
                          
                          <div className="mb-3">
                              <label className="small text-muted-theme">Notes</label>
                              <textarea className="form-control bg-input text-main border-theme" rows="3" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}></textarea>
                          </div>

                          {selectedMeeting && !selectedMeeting.isPending && (
                             <div className="p-3 bg-input rounded border border-theme mb-3">
                                 <label className="small text-muted-theme fw-bold">Status Actions</label>
                                 <div className="d-flex flex-wrap gap-2 mt-2">
                                     {formData.status !== 'Done' && (
                                         <button className="btn btn-sm btn-success" onClick={() => handleStatusChange('Done')}>
                                             <i className="bi bi-check-lg me-1"></i> Complete & Log
                                         </button>
                                     )}
                                     {formData.status !== 'Cancelled' && (
                                         <button className="btn btn-sm btn-outline-danger" onClick={() => {
                                             const reason = prompt("Reason for cancellation?");
                                             if(reason) handleStatusChange('Cancelled', reason);
                                         }}>
                                             Cancel
                                         </button>
                                     )}
                                      {formData.status !== 'Delayed' && (
                                         <button className="btn btn-sm btn-outline-warning" onClick={() => handleStatusChange('Delayed')}>
                                             Delay
                                         </button>
                                     )}
                                 </div>
                                 {formData.status === 'Cancelled' && (
                                     <div className="text-danger small mt-2">Reason: {formData.cancelReason}</div>
                                 )}
                             </div>
                          )}
                      </div>
                      <div className="modal-footer border-theme">
                          <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                          <button className="btn btn-primary" onClick={handleSave}>Save Meeting</button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default SchedulerView;
